import { forwardRef, useEffect } from 'react';
import type { MouseEvent, KeyboardEvent, PropsWithoutRef, ChangeEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { hideVisually } from 'polished';

import FormField, { StyledFormFieldInfo } from '../FormField';
import type { FormFieldProps } from '../FormField';
import { StyledFormControl } from '../FormControl';
import type { FormControlProps } from '../FormControl';
import { defaultThemeProp } from '../../theme';
import { createClassName, getActiveElement, tryCatch, withTestIds } from '../../utils';
import type { NoChildrenProp, WithAttributes, RefElement, TestIdProp } from '../../types';
import { useConsolidatedRef, useDirection, useI18n, useTestIds, useUID } from '../../hooks';
import { StyledLabel } from '../Label';
import { calculateFontSize, readableColor, getReadableStatusColor } from '../../styles';
import HiddenText from '../HiddenText';
import type { elements } from '../FormField/FormField.test-ids';

import { getRadioCheckTestIds } from './RadioCheck.test-ids';

export type RadioCheckProps = WithAttributes<
  'input',
  NoChildrenProp &
    TestIdProp<typeof elements> & {
      /** Pass a string or a fragment with an Icon and string. */
      label?: FormControlProps['label'];
      /** Set visual state based on a validation state. */
      status?: FormControlProps['status'];
      /** Sets the type of input. */
      type: 'radio' | 'checkbox';
      /**
       * Sets control to an [indeterminate state](https://css-tricks.com/almanac/selectors/i/indeterminate/#indeterminate-checkboxes).
       * @default false
       */
      indeterminate?: boolean;
      /** Sets checked prop via onChange when using as controlled component. */
      checked?: boolean;
      /** Initialize checked prop when using as uncontrolled component. */
      defaultChecked?: boolean;
      /**
       * Controls the styling of the RadioCheck.
       * @default 'simple'
       */
      variant?: 'simple' | 'card';
      /** Pass a heading and content to show additional information on the field. */
      additionalInfo?: FormControlProps['additionalInfo'];
      /** info like error, success, warning messages */
      info?: FormControlProps['info'];
      /** Callback invoked when this particular radio button is selected. */
      onChange?: ChangeEventHandler<HTMLInputElement>;
      /** Pass a string used as input's aria-describedby. */
      ariaDescribedby?: string;
      /** Suppresses announcements for status changes. */
      suppressAnnouncements?: FormFieldProps['suppressAnnouncements'];
    }
>;

export const StyledPseudoRadioCheck = styled.div<
  Pick<RadioCheckProps, 'readOnly' | 'status'> & { $labelEmpty?: boolean }
>(({ theme, status, $labelEmpty }) => {
  const {
    components: {
      'radio-check': {
        size,
        'touch-size': touchSize,
        'background-color': backgroundColor,
        'border-color': borderColor,
        'border-width': borderWidth
      }
    }
  } = theme;

  let statusBorderColor = borderColor;
  if (status === 'error' || status === 'warning') {
    statusBorderColor = getReadableStatusColor(theme, status);
  }

  return css`
    display: flex;
    flex-shrink: 0;
    position: relative;
    width: ${size};
    height: ${size};
    margin-inline-end: ${$labelEmpty ? '0' : '1ch'};
    border: ${borderWidth} solid ${statusBorderColor};
    background-color: ${backgroundColor};

    @media (pointer: coarse) {
      width: ${touchSize};
      height: ${touchSize};
    }

    &::after {
      content: '';
      display: none;
    }
  `;
});

StyledPseudoRadioCheck.defaultProps = defaultThemeProp;

export const StyledRadioCheckInput = styled.input(({ theme, disabled, readOnly }) => {
  const {
    base: { 'border-radius': baseBorderRadius },
    components: {
      'form-control': {
        ':focus': { 'box-shadow': shadow, 'border-color': focusBorderColor },
        ':read-only': { 'background-color': readOnlyBackgroundColor }
      },
      'radio-check': {
        size,
        ':checked': {
          'background-color': checkedBackgroundColor,
          'border-color': checkedBorderColor
        }
      },
      checkbox: { 'border-radius': checkRadius },
      'radio-button': { 'border-radius': radioRadius }
    }
  } = theme;

  const backgroundColor = readOnly ? readOnlyBackgroundColor : checkedBackgroundColor;
  const foreground = tryCatch(() => readableColor(backgroundColor));

  const prcSelector = `+ ${StyledLabel} ${StyledPseudoRadioCheck}`;
  const { ltr } = useDirection();

  return css`
    ${hideVisually}
    margin: 0;

    /* Sets rect size for JAWS focus outline */
    width: ${size};
    height: ${size};

    ${!disabled &&
    css`
      &:focus ${prcSelector} {
        box-shadow: ${shadow};
        border-color: ${focusBorderColor};
      }
    `}
    &:checked
      ${prcSelector},
      &:checked:disabled
      ${prcSelector},
      &[type='checkbox']:indeterminate
      ${prcSelector},
      &[type='checkbox']:indeterminate:disabled
      ${prcSelector} {
      border-color: ${readOnly ? 'inherit' : checkedBorderColor};
      background-color: ${backgroundColor};

      &::after {
        display: block;
      }
    }

    &[type='radio'] ${prcSelector}, &[type='radio'] ${prcSelector}::after {
      border-radius: ${radioRadius};
    }

    &[type='radio'] ${prcSelector}::after {
      content: '';
      position: absolute;
      inset: 0;
      margin: auto;
      width: calc(${size} * 0.3);
      height: calc(${size} * 0.3);
      border-radius: ${radioRadius};
      border: calc(${size} * 0.2) solid ${foreground};
    }

    &[type='checkbox'] ${prcSelector} {
      border-radius: min(calc(${baseBorderRadius} * ${checkRadius}), 0.25rem);
    }

    &[type='checkbox']:not(:indeterminate) ${prcSelector} {
      &::after {
        width: 40%;
        height: 75%;
        ${ltr
          ? css`
              transform: rotate(45deg) translate(50%, -30%);
            `
          : css`
              transform: rotate(45deg) translate(-50%, 30%);
            `}
        border-right: 0.15em solid ${foreground};
        border-bottom: 0.15em solid ${foreground};
      }
    }

    &[type='checkbox']:indeterminate ${prcSelector} {
      display: flex;

      &::after {
        width: 90%;
        height: 0.15em;
        margin: auto;
        background-color: ${foreground};
      }
    }
  `;
});

StyledRadioCheckInput.defaultProps = defaultThemeProp;

export const StyledRadioCheck = styled.div<Pick<RadioCheckProps, 'disabled' | 'readOnly'>>(
  ({ theme, disabled, readOnly }) => {
    const {
      base,
      components: {
        'radio-check': {
          label: { color: labelColor, 'font-weight': labelFontWeight }
        },
        'form-control': {
          ':hover': { 'border-color': hoverBorderColor }
        }
      }
    } = theme;

    const fontSize = calculateFontSize(base['font-size'], base['font-scale']);

    return css`
      > ${StyledLabel} {
        cursor: pointer;
        display: flex;
        align-items: center;
        font-weight: ${labelFontWeight};
        word-break: break-word;
        font-size: ${fontSize.s};
        color: ${labelColor};
        margin: 0;
        min-height: ${base['hit-area']['mouse-min']};

        @media (pointer: coarse) {
          min-height: ${base['hit-area']['finger-min']};
        }
      }

      > ${StyledFormFieldInfo} {
        margin: 0;
        padding-inline-start: calc(${base.spacing} / 4);
      }

      ${!(disabled || readOnly) &&
      css`
        &:not(:focus-within) > ${StyledLabel}:hover ${StyledPseudoRadioCheck} {
          border-color: ${hoverBorderColor};
        }
      `}
    `;
  }
);

StyledRadioCheck.defaultProps = defaultThemeProp;

export const StyledRadioCheckCard = styled.label<
  Pick<RadioCheckProps, 'disabled' | 'readOnly' | 'status'>
>(({ theme, disabled, readOnly, status }) => {
  const {
    base: { palette, shadow, spacing },
    components: {
      card,
      'radio-check': { label },
      'form-control': {
        ':hover': { 'border-color': hoverBorderColor }
      }
    }
  } = theme;

  const useBorderColor =
    status === 'error' ? getReadableStatusColor(theme, 'error') : palette['border-line'];

  return css`
    min-width: min-content;
    cursor: pointer;
    background-color: ${card.background};
    border-radius: ${card['border-radius']};
    border: 0.0625rem solid ${useBorderColor};
    padding: ${spacing};

    ${disabled &&
    css`
      cursor: not-allowed;
    `}
    ${!(disabled || readOnly) &&
    css`
      :hover:not(:focus-within) {
        border-color: ${hoverBorderColor};

        ${StyledLabel} ${StyledPseudoRadioCheck} {
          border-color: ${hoverBorderColor};
        }
      }

      :focus-within {
        box-shadow: ${shadow.focus};
        border-color: transparent;
      }
    `}
        > ${StyledLabel} {
      display: flex;
      align-items: center;
      color: ${label.color};
      font-weight: ${label['font-weight']};
      margin: 0;
    }

    > ${StyledFormFieldInfo} {
      margin: 0;
      padding-inline-start: calc(${spacing} / 4);
    }
  `;
});

StyledRadioCheckCard.defaultProps = defaultThemeProp;

const RadioCheck = forwardRef<RefElement<RadioCheckProps>, PropsWithoutRef<RadioCheckProps>>(
  function RadioCheck(props, ref) {
    const uid = useUID();
    const t = useI18n();

    const {
      testId,
      type,
      id = uid,
      label,
      'aria-labelledby': ariaLabelledBy,
      required = false,
      disabled = false,
      readOnly = false,
      indeterminate = false,
      checked,
      defaultChecked,
      variant = 'simple',
      onClick,
      onKeyDown,
      onChange,
      status,
      info,
      additionalInfo,
      ariaDescribedby,
      suppressAnnouncements,
      className,
      ...restProps
    } = props;

    const testIds = useTestIds(testId, getRadioCheckTestIds);
    const isRadio = type === 'radio';
    const card = variant === 'card';
    const inputRef = useConsolidatedRef(ref);
    const readonlyDescId = useUID();

    useEffect(() => {
      if (!isRadio && inputRef.current) inputRef.current.indeterminate = !!indeterminate;
    }, [inputRef, indeterminate, isRadio]);

    // Prevents blur when a user mouse clicks the label to select an input that is actively focused.
    useEffect(() => {
      if (!inputRef.current) return;

      const labelEl = inputRef.current.nextSibling;

      if (labelEl?.nodeName.toUpperCase() !== 'LABEL') return;

      const ac = new AbortController();

      labelEl.addEventListener(
        'mousedown',
        e => {
          if (inputRef.current === getActiveElement()) {
            e.preventDefault();
          }
        },
        { signal: ac.signal }
      );

      return () => {
        ac.abort();
      };
    }, []);

    const labelEmpty = label === null || label === undefined || label === '';

    return (
      <FormField
        testId={testIds}
        as={card ? StyledRadioCheckCard : StyledRadioCheck}
        label={
          <>
            <StyledPseudoRadioCheck
              status={status}
              as={StyledFormControl}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              $labelEmpty={labelEmpty}
            />
            {label}
            {readOnly && <HiddenText id={readonlyDescId}>{t('read_only')}</HiddenText>}
          </>
        }
        labelAs={card || ariaLabelledBy ? 'div' : undefined}
        id={id}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        status={status}
        info={info}
        isRadioCheck
        inline
        labelAfter
        additionalInfo={additionalInfo}
        suppressAnnouncements={suppressAnnouncements}
      >
        <StyledRadioCheckInput
          data-testid={testIds.control}
          {...restProps}
          className={createClassName('radio-check', className, {
            variant,
            type
          })}
          id={id}
          aria-labelledby={ariaLabelledBy}
          type={type}
          required={required}
          checked={checked}
          defaultChecked={defaultChecked}
          aria-describedby={readOnly ? `${ariaDescribedby} ${readonlyDescId}` : ariaDescribedby}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onClick={(e: MouseEvent<HTMLInputElement>) => {
            if (readOnly) e.preventDefault();
            onClick?.(e);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (type === 'checkbox' && readOnly && e.key === ' ') e.preventDefault();
            if (type === 'radio' && readOnly && e.key.includes('Arrow')) e.preventDefault();
            onKeyDown?.(e);
          }}
          ref={inputRef}
        />
      </FormField>
    );
  }
);

export default withTestIds(RadioCheck, getRadioCheckTestIds);
